import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Settings2, 
  Clock, 
  Globe2, 
  Calendar, 
  ShieldCheck, 
  Save, 
  Plus, 
  CheckCircle2, 
  Users2,
  AlertCircle,
  X,
  Mail,
  Send,
  Lock,
  Code2,
  Eye,
  RefreshCw,
  Copy,
  FileText,
  ListFilter,
  Layout,
  Network
} from 'lucide-react';
import {
  administrationService,
  OrganizationSettings,
  OrganizationData,
  Department,
  EmailTemplateItem,
  EmailOutboxItem
} from './administrationService';
import { EmailGovernanceWorkspace } from './EmailGovernanceWorkspace';
import { AdminControlTowerWorkspace } from './AdminControlTowerWorkspace';
import { RoleDashboardLayoutWorkspace } from './RoleDashboardLayoutWorkspace';
import { ReportingTaxonomyWorkspace } from './ReportingTaxonomyWorkspace';
import { MeetingTypesWorkspace } from './MeetingTypesWorkspace';
import { QuestionEditorModal } from '../ingestion/components/QuestionEditorModal';
import {
  localizeFormTitle,
  localizeFormDesc,
  localizeQuestionLabel
} from '../ingestion/IngestionReportingPage';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const AdministrationPage: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'settings' | 'departments' | 'emails' | 'forms' | 'control_tower' | 'dashboard_layouts' | 'reporting_settings' | 'meeting_types'>('reporting_settings');
  const [, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Form Templates & Questions State
  const [forms, setForms] = useState<any[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<{ question: any; formTitle?: string } | null>(null);

  // Settings State
  const [org, setOrg] = useState<OrganizationData | null>(() => {
    try {
      const saved = localStorage.getItem('auth_user');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.organization) {
          return {
            id: parsed.organization.id || '',
            organization_code: parsed.organization.code || 'ORG-XYZ',
            legal_name: parsed.organization.name || 'XYZ Aviation',
            trading_name: parsed.organization.name || 'XYZ Aviation',
            default_timezone: 'UTC',
            default_currency: 'USD',
            default_language: 'en',
            status: 'ACTIVE',
          };
        }
      }
    } catch {}
    return null;
  });
  const [settings, setSettings] = useState<Partial<OrganizationSettings>>({});

  // Departments State
  const [departments, setDepartments] = useState<Department[]>([]);
  const { sortedItems: sortedDepts, sortField: deptSortField, sortDirection: deptSortDir, toggleSort: toggleDeptSort } = useTableSort(departments);
  const [showAddDeptModal, setShowAddDeptModal] = useState(false);
  const [deptForm, setDeptForm] = useState({
    name: '',
    department_code: '',
    department_type: 'OPERATIONAL',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Listen for Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddDeptModal) {
        setShowAddDeptModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAddDeptModal]);

  const loadForms = async () => {
    try {
      const formsList = await administrationService.getFormTemplates();
      setForms(formsList);
      if (formsList.length > 0 && !selectedFormId) {
        setSelectedFormId(formsList[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load form templates:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const [settingsRes, deptRes, formsList] = await Promise.all([
        administrationService.getSettings(),
        administrationService.getDepartments(),
        administrationService.getFormTemplates().catch(() => []),
      ]);
      setOrg(settingsRes.organization);
      setSettings(settingsRes.settings);
      setDepartments(deptRes);
      if (formsList && formsList.length > 0) {
        setForms(formsList);
        setSelectedFormId(formsList[0].id);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to load administration data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMsg(null);
      setSuccessMsg(null);
      const updated = await administrationService.updateSettings(settings);
      setSettings(updated);
      setSuccessMsg('Organization settings successfully saved.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to save configuration settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDept = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await administrationService.createDepartment(deptForm);
      setShowAddDeptModal(false);
      setDeptForm({ name: '', department_code: '', department_type: 'OPERATIONAL', description: '' });
      await loadData();
      setSuccessMsg('New department successfully created.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to create department.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateDept = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this department?')) return;
    try {
      await administrationService.deactivateDepartment(id);
      await loadData();
    } catch (err: any) {
      window.alert(err.message || 'Failed to deactivate department.');
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1280px', margin: '0 auto' }}>
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '12px',
        padding: '24px 28px',
        color: '#ffffff',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <div>
          <div style={{ display: 'inline-block', background: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '8px' }}>
            ORGANIZATION MANAGEMENT & CONFIGURATION
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: '0 0 6px 0' }}>
            {org?.legal_name || 'Organization'} ({org?.organization_code || 'ORG'})
          </h1>
          <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>
            Configure organization regional preferences, operational governance policies, departments, and email routing.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveSubTab('settings')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'settings' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <Settings2 size={16} /> System Settings
          </button>
          <button
            onClick={() => setActiveSubTab('departments')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'departments' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <Users2 size={16} /> Departments ({departments.length})
          </button>
          <button
            onClick={() => setActiveSubTab('emails')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'emails' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <Mail size={16} /> Email Configuration
          </button>
          <button
            onClick={() => setActiveSubTab('control_tower')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'control_tower' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <ShieldCheck size={16} /> Admin Control Tower
          </button>
          <button
            onClick={() => {
              setActiveSubTab('forms');
              loadForms();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'forms' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <FileText size={16} /> Form Templates & Questions ({forms.length})
          </button>
          <button
            onClick={() => setActiveSubTab('reporting_settings')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'reporting_settings' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <Network size={16} /> Reporting Settings
          </button>
          <button
            onClick={() => setActiveSubTab('dashboard_layouts')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'dashboard_layouts' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <Layout size={16} /> Role Dashboard Layouts
          </button>
          <button
            onClick={() => setActiveSubTab('meeting_types')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              background: activeSubTab === 'meeting_types' ? '#2563eb' : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff'
            }}
          >
            <Calendar size={16} /> Meeting Types (Toplantı Türleri)
          </button>
        </div>
      </div>

      {successMsg && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <CheckCircle2 size={18} /> {successMsg}
        </div>
      )}

      {errorMsg && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <AlertCircle size={18} /> {errorMsg}
        </div>
      )}

      {activeSubTab === 'settings' && (
        <form onSubmit={handleSaveSettings}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            {/* General Regional & Localization Card */}
            <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                <Globe2 size={18} color="#2563eb" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Regional & Localization Settings</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Timezone</label>
                  <select
                    value={settings.timezone || 'UTC'}
                    onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value="UTC">UTC (Coordinated Universal Time)</option>
                    <option value="Europe/Istanbul">Europe/Istanbul (GMT+3)</option>
                    <option value="Europe/London">Europe/London (GMT+0)</option>
                    <option value="Europe/Berlin">Europe/Berlin (GMT+1)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Default Currency</label>
                  <select
                    value={settings.currency || 'USD'}
                    onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="TRY">TRY (₺)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>System Language</label>
                  <select
                    value={settings.language_code || 'en'}
                    onChange={(e) => setSettings({ ...settings, language_code: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value="en">English (US/UK)</option>
                    <option value="tr">Türkçe (TR)</option>
                    <option value="de">Deutsch (DE)</option>
                    <option value="fr">Français (FR)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Date Format</label>
                  <select
                    value={settings.date_format || 'YYYY-MM-DD'}
                    onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2026-10-07)</option>
                    <option value="DD.MM.YYYY">DD.MM.YYYY (07.10.2026)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (10/07/2026)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Operational & Governance Policies Card */}
            <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                <ShieldCheck size={18} color="#059669" />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>Operational Governance Policies</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Document Review Period (Days)</label>
                  <input
                    type="number"
                    value={settings.default_policies?.document_review_period_days || 365}
                    onChange={(e) => setSettings({
                      ...settings,
                      default_policies: {
                        ...settings.default_policies,
                        document_review_period_days: Number(e.target.value)
                      }
                    })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Task Reminder Period (Days)</label>
                  <input
                    type="number"
                    value={settings.default_policies?.task_reminder_days || 7}
                    onChange={(e) => setSettings({
                      ...settings,
                      default_policies: {
                        ...settings.default_policies,
                        task_reminder_days: Number(e.target.value)
                      }
                    })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Escalation Threshold (Hours)</label>
                  <input
                    type="number"
                    value={settings.default_policies?.escalation_threshold_hours || 48}
                    onChange={(e) => setSettings({
                      ...settings,
                      default_policies: {
                        ...settings.default_policies,
                        escalation_threshold_hours: Number(e.target.value)
                      }
                    })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Fiscal Year Start Month</label>
                  <select
                    value={settings.fiscal_year_start_month || 1}
                    onChange={(e) => setSettings({ ...settings, fiscal_year_start_month: Number(e.target.value) })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value={1}>January (1)</option>
                    <option value={2}>February (2)</option>
                    <option value={3}>March (3)</option>
                    <option value={4}>April (4)</option>
                    <option value={5}>May (5)</option>
                    <option value={6}>June (6)</option>
                    <option value={7}>July (7)</option>
                    <option value={8}>August (8)</option>
                    <option value={9}>September (9)</option>
                    <option value={10}>October (10)</option>
                    <option value={11}>November (11)</option>
                    <option value={12}>December (12)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(37,99,235,0.2)'
              }}
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      )}

      {activeSubTab === 'departments' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
              Manage operational, technical, safety, and administrative departments across your organization.
            </p>
            <button
              onClick={() => setShowAddDeptModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              <Plus size={16} /> Add Department
            </button>
          </div>

          <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                  <SortableTh field="department_code" currentField={deptSortField} currentDirection={deptSortDir} onSort={toggleDeptSort} style={{ padding: '12px 16px' }}>
                    Code
                  </SortableTh>
                  <SortableTh field="name" currentField={deptSortField} currentDirection={deptSortDir} onSort={toggleDeptSort} style={{ padding: '12px 16px' }}>
                    Department Name
                  </SortableTh>
                  <SortableTh field="department_type" currentField={deptSortField} currentDirection={deptSortDir} onSort={toggleDeptSort} style={{ padding: '12px 16px' }}>
                    Type
                  </SortableTh>
                  <SortableTh field="status" currentField={deptSortField} currentDirection={deptSortDir} onSort={toggleDeptSort} style={{ padding: '12px 16px' }}>
                    Status
                  </SortableTh>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedDepts.map((dept) => (
                  <tr key={dept.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontWeight: 600, color: '#2563eb' }}>
                      {dept.department_code}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1e293b' }}>
                      {dept.name}
                      {dept.description && <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 400 }}>{dept.description}</div>}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {dept.department_type || 'OPERATIONAL'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: dept.status === 'ACTIVE' ? '#dcfce7' : '#f1f5f9',
                        color: dept.status === 'ACTIVE' ? '#166534' : '#64748b'
                      }}>
                        {dept.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      {dept.status === 'ACTIVE' && (
                        <button
                          onClick={() => handleDeactivateDept(dept.id)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            color: '#ef4444',
                            cursor: 'pointer'
                          }}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSubTab === 'emails' && (
        <EmailGovernanceWorkspace defaultSubTab="global_governance" />
      )}

      {activeSubTab === 'forms' && (
        <div>
          {/* Header Description */}
          <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid #6366f1' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: '0 0 6px 0' }}>
              📋 Reporting Form Templates & Dynamic Question Bank
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
              Configure corporate reporting forms, question types, mandatory validation, and custom dropdown choices (including standard Flight Phases, Occurrence Classifications, and custom "Other" manual input options).
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px' }}>
            {/* Left Column: Form Templates List */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>
                Form Templates ({forms.length})
              </div>
              <div className="card-scroll-pane" style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '580px', overflowY: 'auto', paddingRight: '4px' }}>
                {forms.map((f) => {
                  const isSelected = f.id === selectedFormId;
                  const isNonSafety = f.report_category === 'NON_SAFETY';
                  return (
                    <div
                      key={f.id}
                      onClick={() => setSelectedFormId(f.id)}
                      style={{
                        padding: '12px 14px',
                        borderRadius: '8px',
                        background: isSelected ? '#eff6ff' : '#ffffff',
                        border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: '#64748b' }}>
                          {f.form_code}
                        </span>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: isNonSafety ? '#e0e7ff' : '#dbeafe',
                            color: isNonSafety ? '#4338ca' : '#1d4ed8',
                          }}
                        >
                          {f.report_category || 'SAFETY'}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '2px' }}>
                        {localizeFormTitle(f.title)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        {f.current_version?.questions?.length || 0} Dynamic Questions
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column: Questions of Selected Form */}
            <div>
              {(() => {
                const currentForm = forms.find((f) => f.id === selectedFormId) || forms[0];
                if (!currentForm) {
                  return (
                    <div className="card" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                      Select a form template on the left to configure its questions.
                    </div>
                  );
                }
                const questions = currentForm.current_version?.questions || [];
                return (
                  <div className="card" style={{ margin: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                            {localizeFormTitle(currentForm.title)}
                          </h3>
                          <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                            {currentForm.form_code}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                          {localizeFormDesc(currentForm.description)}
                        </p>
                      </div>
                      <span className="badge-pill badge-info" style={{ fontSize: '11px' }}>
                        Type: {currentForm.report_type}
                      </span>
                    </div>

                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', marginBottom: '10px' }}>
                      Questions Catalog & Options Configuration ({questions.length})
                    </div>

                    <div className="card-scroll-pane" style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '520px', overflowY: 'auto', paddingRight: '4px' }}>
                      {questions.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                          No specific questions configured for this form version.
                        </div>
                      ) : (
                        questions.map((q: any) => {
                          let optionsList: string[] = [];
                          if (q.options_json) {
                            try {
                              optionsList = JSON.parse(q.options_json);
                            } catch {}
                          }
                          const isSelect = q.question_type === 'SELECT' || q.question_key === 'flight_phase';
                          return (
                            <div
                              key={q.id}
                              style={{
                                padding: '14px 16px',
                                background: '#f8fafc',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '16px',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                                    {localizeQuestionLabel(q.label)}
                                  </span>
                                  {q.is_required && (
                                    <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '12px' }}>* Required</span>
                                  )}
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      background: '#e0e7ff',
                                      color: '#4338ca',
                                    }}
                                  >
                                    {q.question_type}
                                  </span>
                                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                                    [{q.section_name || 'General'}]
                                  </span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                                  key: {q.question_key}
                                </div>
                                {isSelect && (
                                  <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                                      Options ({optionsList.length}):
                                    </span>
                                    {optionsList.slice(0, 5).map((opt, i) => (
                                      <span
                                        key={i}
                                        style={{
                                          fontSize: '11px',
                                          background: '#ffffff',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '4px',
                                          padding: '1px 6px',
                                          color: opt.toLowerCase() === 'other' ? '#4f46e5' : '#334155',
                                          fontWeight: opt.toLowerCase() === 'other' ? 700 : 400,
                                        }}
                                      >
                                        {opt}
                                      </span>
                                    ))}
                                    {optionsList.length > 5 && (
                                      <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                                        +{optionsList.length - 5} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={() => setEditingQuestion({ question: q, formTitle: currentForm.title })}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '7px 14px',
                                  background: '#2563eb',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                ⚙️ Configure Options & Type
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {activeSubTab === 'control_tower' && (
        <AdminControlTowerWorkspace />
      )}

      {activeSubTab === 'dashboard_layouts' && (
        <RoleDashboardLayoutWorkspace />
      )}

      {activeSubTab === 'reporting_settings' && (
        <ReportingTaxonomyWorkspace />
      )}

      {activeSubTab === 'meeting_types' && (
        <MeetingTypesWorkspace />
      )}

      {/* Admin Question Editor Modal */}
      {editingQuestion && (
        <QuestionEditorModal
          question={editingQuestion.question}
          formTitle={editingQuestion.formTitle}
          isOpen={Boolean(editingQuestion)}
          onClose={() => setEditingQuestion(null)}
          onSaved={() => {
            loadForms();
          }}
        />
      )}

      {/* Add Department Modal */}
      {showAddDeptModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '500px',
            padding: '24px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowAddDeptModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#64748b'
              }}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
              Add New Department
            </h3>
            <form onSubmit={handleCreateDept}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Department Name *</label>
                <input
                  type="text"
                  required
                  value={deptForm.name}
                  onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                  placeholder="e.g. Flight Operations Directorate"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Department Code (Optional)</label>
                <input
                  type="text"
                  value={deptForm.department_code}
                  onChange={(e) => setDeptForm({ ...deptForm, department_code: e.target.value })}
                  placeholder="e.g. DEPT-FLIGHT-OPS"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Department Type</label>
                <select
                  value={deptForm.department_type}
                  onChange={(e) => setDeptForm({ ...deptForm, department_type: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                >
                  <option value="OPERATIONAL">OPERATIONAL</option>
                  <option value="TECHNICAL">TECHNICAL</option>
                  <option value="SAFETY_QUALITY">SAFETY_QUALITY</option>
                  <option value="ADMINISTRATIVE">ADMINISTRATIVE</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Description</label>
                <textarea
                  rows={2}
                  value={deptForm.description}
                  onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                  placeholder="Department scope and responsibilities"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddDeptModal(false)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: '8px 16px', background: '#2563eb', color: '#ffffff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                >
                  {saving ? 'Creating...' : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
